import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { bool, func, node, string } from 'prop-types';
import { useFela } from 'react-fela';
import { makeId, makeIdList, useId } from '../../utils/auto-id';
import { deprecate } from '../../utils/deprecate';
import { Block } from '../block';
import { Flex } from '../flex';
import { Message } from '../message';
import { Spacer } from '../spacer';
import { Spinner } from '../spinner';
const LEGACY_PADDING = 16;
const FIREFOX_INNER_PADDING = 4;

const arrowIcon = color => `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='9' viewBox='0 0 16 9' fill='none' %3E %3Cpath d='M15 1L8 8L1 1' stroke='${encodeURIComponent(color)}' stroke-linecap='round' stroke-linejoin='round'%3E %3C/path%3E %3C/svg%3E")`;

const styles = _ref => {
  let {
    loading,
    theme,
    isValid,
    hasLabel
  } = _ref;
  return { ...theme.typeScale.columbus.standard.styles,
    appearance: 'none',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
    boxSizing: 'border-box',
    display: 'block',
    width: '100%',
    margin: 0,
    outline: 0,
    paddingTop: theme.tokens.inputPaddingVertical * 2,
    paddingBottom: theme.tokens.inputPaddingVertical / 2,
    paddingLeft: theme.tokens.inputPaddingHorizontal - 1,
    paddingRight: theme.tokens.inputPaddingHorizontal - 1 + (loading ? 0 : 30),
    color: theme.tokens.inputForeground,
    backgroundColor: theme.tokens.inputBackground,
    backgroundImage: arrowIcon(theme.tokens.inputForeground),
    backgroundSize: '15px 7px',
    backgroundPosition: 'right 18px top 51%',
    backgroundRepeat: 'no-repeat',
    userSelect: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    borderColor: isValid ? theme.tokens.inputBorder : theme.color.foreground.alert,
    ':disabled': {
      cursor: 'not-allowed',
      borderColor: theme.tokens.inputDisabledBorder,
      backgroundColor: theme.tokens.inputDisabledBackground,
      color: theme.tokens.inputDisabledForeground,
      '& + label': {
        cursor: 'not-allowed'
      }
    },
    '& option:disabled': {
      color: theme.tokens.inputDisabledForeground
    },
    ':invalid': {
      color: theme.tokens.inputPlaceholder
    },
    ':focus:invalid': {
      color: theme.tokens.inputPlaceholder
    },
    '::-ms-expand': {
      display: 'none'
    },
    '::-ms-value': {
      background: 'none',
      color: theme.tokens.inputForeground
    },
    ':valid': {
      color: theme.tokens.inputForeground
    },
    '& option': {
      color: theme.tokens.inputForeground
    },
    ':-moz-focusring': {
      color: 'transparent',
      textShadow: '0 0 0 #000'
    },
    ':focus': {
      color: theme.tokens.inputForeground,
      borderColor: theme.tokens.inputBorderFocus,
      '& + label': {
        transform: 'translateY(8px)',
        fontSize: 12
      }
    },
    // firefox has some weird inner padding
    '@supports (-moz-appearance: none)': {
      paddingLeft: theme.tokens.inputPaddingHorizontal - FIREFOX_INNER_PADDING - 1
    },
    extend: [{
      condition: loading,
      style: {
        backgroundImage: 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%'
      }
    }, {
      condition: !isValid,
      style: {
        ':not(:focus)': {
          borderWidth: 2,
          backgroundPosition: 'right 17px top 51%',
          borderColor: theme.color.foreground.alert,
          paddingTop: theme.tokens.inputPaddingVertical * 2 - 1,
          paddingBottom: theme.tokens.inputPaddingVertical / 2 - 1,
          paddingLeft: theme.tokens.inputPaddingHorizontal - 2,
          paddingRight: theme.tokens.inputPaddingHorizontal - 2 + (loading ? 0 : 30),
          // firefox has some weird inner padding
          '@supports (-moz-appearance: none)': {
            paddingLeft: theme.tokens.inputPaddingHorizontal - FIREFOX_INNER_PADDING - 2
          }
        }
      }
    }, {
      condition: !hasLabel,
      style: {
        paddingTop: LEGACY_PADDING - 1,
        paddingBottom: LEGACY_PADDING - 1,
        extend: [{
          condition: !isValid,
          style: {
            ':not(:focus)': {
              borderWidth: 2,
              borderColor: theme.color.foreground.alert,
              paddingTop: LEGACY_PADDING - 2,
              paddingBottom: LEGACY_PADDING - 2,
              paddingLeft: theme.tokens.inputPaddingHorizontal - 2,
              paddingRight: theme.tokens.inputPaddingHorizontal - 2 + (loading ? 0 : 30)
            }
          }
        }]
      }
    }]
  };
};

const labelStyle = _ref2 => {
  let {
    isEmpty,
    isValid,
    theme
  } = _ref2;
  return {
    paddingLeft: theme.tokens.inputPaddingHorizontal,
    paddingRight: theme.tokens.inputPaddingHorizontal,
    color: isValid ? theme.color.foreground.secondary : theme.color.foreground.alert,
    transitionProperty: 'font-size, transform',
    transitionDuration: '60ms',
    transitionTimingFunction: 'ease-out',
    transform: 'translateY(' + (isEmpty ? 17 : 8) + 'px)',
    fontSize: isEmpty ? 16 : 12,
    letterSpacing: '0.02em',
    fontFamily: theme.fontTypes.NOVUM,
    fontWeight: 300,
    position: 'absolute',
    pointerEvents: 'none'
  };
};

export const SelectInput = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    children,
    disabled,
    value = '',
    label = '',
    description = '',
    errorMessage = '',
    isValid = true,
    loading = false,
    allowEmpty = false,
    ...props
  } = _ref3;
  const {
    theme
  } = useFela(); // legacy check

  const hasLabel = label.length > 0;
  deprecate('Unlabelled select-input fields are deprecated and will be removed in 3.0.0. Pass a label prop.', !hasLabel);
  const isEmpty = value.length === 0;
  const styleProps = {
    isEmpty,
    hasLabel,
    isValid,
    loading,
    disabled,
    theme
  };
  const id = useId('vcc-ui-select-input', props.id);
  const errorMessageId = makeId(id, 'error');
  const descriptionId = makeId(id, 'description');
  const describedBy = makeIdList([errorMessage && errorMessageId, description && descriptionId]);
  const messages = /*#__PURE__*/React.createElement(React.Fragment, null, errorMessage || description ? /*#__PURE__*/React.createElement(Spacer, {
    size: 0.5
  }) : null, errorMessage ? /*#__PURE__*/React.createElement(Message, {
    type: "error",
    id: errorMessageId
  }, errorMessage) : null, description ? /*#__PURE__*/React.createElement(Message, {
    id: descriptionId
  }, description) : null);

  if (loading) {
    return /*#__PURE__*/React.createElement(Flex, {
      extend: {
        flexDirection: 'column',
        width: '100%'
      }
    }, /*#__PURE__*/React.createElement(Block, {
      ref: ref,
      extend: styles({ ...styleProps,
        hasLabel: false
      })
    }, /*#__PURE__*/React.createElement(Spinner, {
      color: theme.tokens.inputForeground,
      size: 24
    })), messages);
  }

  return /*#__PURE__*/React.createElement(Flex, {
    extend: {
      flexDirection: 'column',
      width: '100%'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    extend: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(Block, _extends({}, props, {
    ref: ref,
    as: "select",
    id: id,
    extend: styles(styleProps),
    disabled: disabled,
    value: value
  }, describedBy && {
    'aria-describedby': describedBy
  }), (isEmpty || allowEmpty) && /*#__PURE__*/React.createElement("option", {
    disabled: !allowEmpty,
    value: ""
  }), children), hasLabel ? /*#__PURE__*/React.createElement(Block, {
    as: "label",
    htmlFor: id,
    extend: labelStyle(styleProps)
  }, label) : null), messages);
});
SelectInput.propTypes = {
  /** Renders a label inside the input.  */
  label: string,

  /** Renders a neutral helper message underneath the input.  */
  description: string,

  /** Renders a red error message for validation underneath the input.  */
  errorMessage: string,
  id: string,
  name: string,
  onChange: func.isRequired,
  value: string,
  disabled: bool,
  loading: bool,
  children: node,
  isValid: bool,
  allowEmpty: bool
};
SelectInput.displayName = 'SelectInput';